import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { apiBugs } from '@/lib/api';
import { HELP } from '@/constants/testIds';
import { Bug, Lightbulb, Send } from 'lucide-react';

export default function Bugs() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ titre: '', description: '', type: 'bug' });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const { data } = await apiBugs.list();
      setList(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.description) return toast.error('Champs requis');
    setSubmitting(true);
    try {
      await apiBugs.create(form);
      toast.success('Déclaration enregistrée — merci !');
      setForm({ titre: '', description: '', type: 'bug' });
      load();
    } catch { toast.error('Erreur'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">Améliorations</div>
        <h1 className="text-4xl font-display font-black tracking-tighter">Bugs & Évolutions</h1>
        <p className="text-stone-600 mt-3">Signalez un bug ou proposez une amélioration. Toutes les déclarations sont suivies par l'équipe HSE Digital.</p>
      </div>

      <form onSubmit={submit} data-testid={HELP.bugForm} className="section-card space-y-4">
        <div>
          <label className="label-industrial">Type de déclaration</label>
          <div className="grid sm:grid-cols-2 gap-3 mt-2">
            <button
              type="button"
              data-testid={HELP.bugType + '-bug'}
              onClick={() => setForm({ ...form, type: 'bug' })}
              className={`p-4 border-2 text-left flex items-center gap-3 ${form.type === 'bug' ? 'border-[#DA291C] bg-red-50' : 'border-stone-200'}`}
            >
              <Bug className="w-5 h-5 text-[#DA291C]" />
              <div>
                <div className="font-bold text-sm">Bug</div>
                <div className="text-xs text-stone-600">Quelque chose ne fonctionne pas</div>
              </div>
            </button>
            <button
              type="button"
              data-testid={HELP.bugType + '-evolution'}
              onClick={() => setForm({ ...form, type: 'evolution' })}
              className={`p-4 border-2 text-left flex items-center gap-3 ${form.type === 'evolution' ? 'border-[#09090B] bg-stone-50' : 'border-stone-200'}`}
            >
              <Lightbulb className="w-5 h-5 text-amber-600" />
              <div>
                <div className="font-bold text-sm">Évolution</div>
                <div className="text-xs text-stone-600">Suggestion d'amélioration</div>
              </div>
            </button>
          </div>
        </div>

        <div>
          <label className="label-industrial">Titre court *</label>
          <input
            data-testid={HELP.bugTitre}
            type="text"
            className="input-industrial w-full"
            value={form.titre}
            onChange={(e) => setForm({ ...form, titre: e.target.value })}
            placeholder="Ex. Impossible de signer sur tablette"
          />
        </div>
        <div>
          <label className="label-industrial">Description détaillée *</label>
          <textarea
            data-testid={HELP.bugDescription}
            rows={5}
            className="input-industrial w-full !min-h-[140px] py-3"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Étapes pour reproduire, comportement observé, comportement attendu..."
          />
        </div>
        <button data-testid={HELP.bugSubmit} type="submit" disabled={submitting} className="btn-industrial-primary flex items-center gap-2">
          <Send className="w-4 h-4" /> Envoyer
        </button>
      </form>

      <div>
        <h2 className="text-xl font-display font-bold mb-4">Mes déclarations</h2>
        {loading ? <div>Chargement...</div> : list.length === 0 ? (
          <div className="section-card text-center py-12 text-stone-500 text-sm">Aucune déclaration pour le moment.</div>
        ) : (
          <div className="space-y-3">
            {list.map((b) => (
              <div key={b.id} className="section-card">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      {b.type === 'bug' ? <Bug className="w-4 h-4 text-[#DA291C]" /> : <Lightbulb className="w-4 h-4 text-amber-600" />}
                      <div className="font-semibold">{b.titre}</div>
                    </div>
                    <div className="text-xs text-stone-500 mt-1">{(b.created_at || '').slice(0, 16).replace('T', ' ')} — {b.created_by_name}</div>
                    <p className="text-sm text-stone-700 mt-2 whitespace-pre-wrap">{b.description}</p>
                  </div>
                  <span className={`badge-status badge-${b.status === 'ouvert' ? 'soumis' : 'cloture'}`}>{b.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
